package games.polyclub.website.frontend.store.context

import games.polyclub.frontend.lib.react.ga4.GA4
import games.polyclub.frontend.lib.react.simplified.SFC
import games.polyclub.frontend.store.shared.LoginResponse
import games.polyclub.message.PronounDetector
import games.polyclub.website.frontend.store.StoreBff
import games.polyclub.website.frontend.store.appScope
import games.polyclub.website.frontend.store.hooks.GAAction
import games.polyclub.website.frontend.store.hooks.GACategory
import games.polyclub.website.frontend.store.hooks.logEvent
import js.core.jso
import kotlinx.coroutines.Deferred
import kotlinx.coroutines.async
import react.*
import web.storage.localStorage

external interface AuthContextData {
    var auth: LoginResponse?
    var setAuth: (LoginResponse?) -> Unit
    var persist: Boolean
    var setPersist: (Boolean) -> Unit
    var showPosLoadEffect: Boolean
    var setShowPosLoadEffect: StateSetter<Boolean>
    var reloadProfile: () -> Deferred<LoginResponse?>
}

val AuthContext = createContext<AuthContextData>()

val AuthProvider = SFC<PropsWithChildren> { props ->
    val (auth, setAuth) = useState<LoginResponse>()
    val (showPosLoadEffect, setShowPosLoadEffect) = useState(true)
    val (persistent, setPersistent) = useState(localStorage.getItem("persist").toBoolean())

    useEffect(auth?.accessToken) {
        StoreBff.token = auth?.accessToken
    }

    useEffectOnce {
        PronounDetector.register {
            (it as? LoginResponse)?.profile
        }
    }

    val reloadProfile = useCallback(setAuth) {
        appScope.async {
            val response = StoreBff.refreshAuthToken()?.getOrNull()
            setAuth(response)
            response
        }
    }

    val updateAuth = useCallback(auth, setAuth) { newAuth: LoginResponse? ->
        if (newAuth != null) {
            GA4.set(jso {
                this.userId = newAuth.profile.id.toInt()
            })
        }
        when {
            auth == null && newAuth != null -> {
                logEvent(
                    category = GACategory.LOGIN,
                    action = GAAction.LOGIN,
                )
            }
            auth != null && newAuth == null -> {
                logEvent(
                    category = GACategory.LOGIN,
                    action = GAAction.LOGOUT
                )
            }
        }
        if (newAuth == null) {
            GA4.set(jso {
                this.userId = null
            })
        }
        setAuth(newAuth)
    }

    AuthContext.Provider {
        value = jso {
            this.showPosLoadEffect = showPosLoadEffect
            this.setShowPosLoadEffect = setShowPosLoadEffect
            this.auth = auth
            this.setAuth = updateAuth
            this.persist = persistent
            this.setPersist = {
                logEvent(
                    category = GACategory.LOGIN,
                    action = GAAction.PERSISTENT_LOGIN,
                    value = if (it) 1 else 0
                )
                localStorage.setItem("persist", it.toString())
                setPersistent(it)
            }
            this.reloadProfile = reloadProfile
        }
        +props.children
    }
}
